import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';

import PageSection from '../../PageSections/PageSection';
import RichTextConverter from '../RichText/RichTextConverter';
import Calculation from './Calculation';
import { calculatorTypes } from './calculatorTypes';
import Form from './Form';
import { createInitialValues, formatNumberAsCurrency, getGrowRevenueResults } from './helpers';
import ItemizedResults from './ItemizedResults';
import * as styles from './RoiCalculator.module.scss';
import Visualization from './Visualization';

export default function RoiCalculator({ section }) {
  // Check for `Template Standard` to support original ROI calculator at fullstory.com/color
  const calculatorType = section.type === 'Template Standard' ? 'default' : section.type;
  const calculatorProperties = calculatorTypes[calculatorType];
  const isDefault = calculatorType === 'default';

  // State to hold the current values of the calculator
  const [calculatorValues, setCalculatorValues] = useState(createInitialValues(calculatorProperties));
  // State to hold the calculated revenue
  const [revenue, setRevenue] = useState(isDefault ? { increase: '$0', loss: '$0' } : null);

  // Memoize the calculator values by their name for easy access
  const calculatorValuesByName = useMemo(
    () => calculatorValues.reduce((acc, item) => ({ ...acc, [item.name]: item }), {}),
    [calculatorValues]
  );

  // Destructure the calculator values for easier access
  const { users, monthlySessions, cvr, orderValue, affectedUsers, repeatCustomers, grossMargin, conversionImpact } =
    calculatorValuesByName;
  // Filter the section content to get the revenue tooltips
  const revenueTooltips = section.content?.filter((item) => item.subtitle === 'increase' || item.subtitle === 'loss');

  // Function to calculate the revenue based on the version (original or new)
  const calculateRevenue = (users, cvr, orderValue, version) => {
    const usersValue = parseInt(users[version], 10);
    const cvrValue = parseFloat(cvr[version]) * 0.01;
    const orderValueValue = parseFloat(orderValue[version]);
    return usersValue * cvrValue * orderValueValue;
  };

  // Function to calculate the revenue loss based on the version (original or new)
  const calculateLoss = (users, cvr, orderValue, affectedUsers, conversionImpact, version) => {
    const affectedUsersPercentage = parseFloat(affectedUsers[version]) * 0.01;
    const conversionImpactPercentage = parseFloat(conversionImpact[version]) * 0.01;
    return calculateRevenue(users, cvr, orderValue, version) * affectedUsersPercentage * conversionImpactPercentage;
  };

  // Function to calculate the final result (revenue increase and loss)
  const calculateResult = useCallback(() => {
    if (isDefault) {
      const startingRevenue = calculateRevenue(users, cvr, orderValue, 'original');
      let increase = calculateRevenue(users, cvr, orderValue, 'new') - startingRevenue;
      let loss = calculateLoss(users, cvr, orderValue, affectedUsers, conversionImpact, 'new');

      if (loss < 0) {
        increase -= loss;
        loss = 0;
      }

      return {
        original: formatNumberAsCurrency(startingRevenue),
        increase: formatNumberAsCurrency(increase),
        loss: formatNumberAsCurrency(loss),
      };
    } else {
      return getGrowRevenueResults(calculatorValuesByName);
    }
  }, [users, monthlySessions, cvr, orderValue, affectedUsers, repeatCustomers, grossMargin, conversionImpact]);

  // Effect to update the revenue whenever the calculator values change
  useEffect(() => {
    setRevenue(calculateResult());
  }, [calculatorValues, calculateResult]);

  return (
    <PageSection
      wide
      className={[styles.section, !isDefault ? styles.calculatorLeft : ''].join(' ')}
      style={isDefault ? { paddingBlock: 0 } : {}}
      innerClassName={styles.container}
      data-analytics-section={`roi-calculator-${calculatorType}`}
    >
      <HydrationProvider>
        <Client>
          {isDefault && (
            <div className={styles.visualization}>
              <Visualization ballCount={users.original < 2 ? 0 : users.step + 1} />
              <div className={styles.digitalExperience}>
                Your brand's
                <br />
                digital experience
              </div>
              <div className={styles.conversionFunnel}>
                Your brand's
                <br />
                conversion funnel
              </div>
              <Calculation revenue={revenue} tooltips={revenueTooltips} version="desktop" />
            </div>
          )}
          <div className={styles.calculator}>
            {(section.title || section.superHeading) && (
              <div className={styles.eyebrow}>{section.title || section.superHeading}</div>
            )}
            <h2>{section.heading}</h2>
            <RichTextConverter copy={section.sectionCopy || section.body} />
            <Form
              calculatorValues={calculatorValues}
              setCalculatorValues={setCalculatorValues}
              section={section}
              properties={calculatorProperties}
            />
            {isDefault && <Calculation revenue={revenue} tooltips={revenueTooltips} version="mobile" />}
          </div>
          {!isDefault && <ItemizedResults results={revenue} />}
        </Client>
      </HydrationProvider>
    </PageSection>
  );
}
